import { MTDynamicSectionForm } from '@/multi-territory/mt-components/mt-admin/MTDynamicSectionForm'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
} from '@mui/material'
import { useState } from 'react'

/**
 * Test page for the Dynamic Section Form.
 * Select a country → Click "Add Section" → The form renders dynamically
 * based on the country's form config JSON.
 */
export function MTSectionFormTestPage() {
  const [selectedCountry, setSelectedCountry] = useState('IN')
  const [formOpen, setFormOpen] = useState(false)
  const [submittedData, setSubmittedData] = useState<Record<
    string,
    string
  > | null>(null)

  const handleSubmit = (data: Record<string, string>) => {
    // CWE-117: sanitize untrusted user form input before logging (strip CR/LF).
    console.log('Form submitted:', sanitizeForLog(data))
    setSubmittedData(data)
  }

  return (
    <Box sx={{ p: 4, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Section Master - Dynamic Form Test
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
        Select a country and click "Add Section" to see the dynamic form
        rendered from the BE JSON config. No FE code changes needed for new
        countries.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry}
              label="Country"
              onChange={e => {
                setSelectedCountry(e.target.value)
                setSubmittedData(null)
              }}
            >
              {COUNTRY_OPTIONS.map(opt => (
                <MenuItem key={opt.country_code} value={opt.country_code}>
                  {opt.country_name} ({opt.country_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => setFormOpen(true)}
            sx={{
              backgroundColor: '#e8873a',
              color: '#000',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#d4762e' },
            }}
          >
            + Add Section
          </Button>
        </Box>
      </Paper>

      {/* Show submitted data for testing */}
      {submittedData && (
        <Paper sx={{ p: 3, backgroundColor: '#f0fdf4' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#166534' }}>
            ✓ Form Submitted Successfully
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              fontSize: '13px',
            }}
          >
            {JSON.stringify(submittedData, null, 2)}
          </Typography>
        </Paper>
      )}

      {/* Dynamic Form Modal */}
      <MTDynamicSectionForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        countryCode={selectedCountry}
        onSubmit={handleSubmit}
      />
    </Box>
  )
}
